import type { HeatmapCell, HeatmapData, Weekday } from '../types/weekOverview';
import { WEEKDAYS_ORDER } from '../constants/weekOverviewConstants';

// Keyboard navigation model for the capacity heatmap's roving tabindex. The
// heatmap renders one flat CSS grid, so "the cell below" cannot be read off the
// DOM — this module holds the geometry instead: which cells are reachable, in
// which visual order, and which one a given key moves to. Pure and DOM-free;
// the components only translate the resolved id back into a focus() call.

const HEATMAP_NAV_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'] as const;

export type HeatmapNavKey = (typeof HEATMAP_NAV_KEYS)[number];

// The grid's columns are the seven weekdays plus two of a different kind: the
// row-header column on the left, which activates rather than reports (a skill's
// expand toggle, a person's calendar), and the week-total column on the right.
// Naming them as columns is what lets Up/Down preserve a column whatever kind of
// cell sits in it.
export const HEATMAP_ROW_HEADER_COLUMN = 'ROW_HEADER';
export const HEATMAP_TOTAL_COLUMN = 'TOTAL';

export type HeatmapNavColumn =
  | Weekday
  | typeof HEATMAP_ROW_HEADER_COLUMN
  | typeof HEATMAP_TOTAL_COLUMN;

// Ids are compared, never parsed, so these separators need no escaping rules —
// a skill or staff id containing one cannot break navigation.
const ROW_ID_SEPARATOR = '|';
const CELL_ID_SEPARATOR = '@';

// One navigable row, in the order it renders. `columns` holds only the row's
// reachable columns, in left-to-right order, so closed days, missing days and
// the columns a row does not render are skipped simply by being absent.
export interface HeatmapNavRow {
  id: string;
  columns: HeatmapNavColumn[];
}

interface HeatmapCellLocation {
  rowIndex: number;
  columnIndex: number;
}

export function isHeatmapNavKey(key: string): key is HeatmapNavKey {
  return HEATMAP_NAV_KEYS.includes(key as HeatmapNavKey);
}

export function skillRowId(skillId: string): string {
  return `skill${ROW_ID_SEPARATOR}${skillId}`;
}

// A person appears under every skill they hold, so the skill is part of the row
// identity — otherwise their sub-rows would all share one id.
export function staffRowId(skillId: string, staffId: string): string {
  return `staff${ROW_ID_SEPARATOR}${skillId}${ROW_ID_SEPARATOR}${staffId}`;
}

export function buildHeatmapCellId(rowId: string, column: HeatmapNavColumn): string {
  return `${rowId}${CELL_ID_SEPARATOR}${column}`;
}

// Every cell a row renders is reachable, whether or not it activates: a closed
// day and a week total both carry a tooltip worth reading. Only a day the row
// has no cell for at all is absent, since nothing renders there.
function navigableColumnsOf(cells: HeatmapCell[]): HeatmapNavColumn[] {
  const daysWithCells = new Set<Weekday>(cells.map((cell) => cell.day));
  const columns: HeatmapNavColumn[] = [
    HEATMAP_ROW_HEADER_COLUMN,
    ...WEEKDAYS_ORDER.filter((day) => daysWithCells.has(day))
  ];
  // Mirrors computeRowTotal in HeatmapRow: a row with nothing open has no total
  // to show, so it renders an empty cell there.
  if (cells.some((cell) => !cell.isClosed)) columns.push(HEATMAP_TOTAL_COLUMN);
  return columns;
}

// Mirrors what WeekHeatmap renders: one row per skill, each expanded skill
// followed by one row per qualified person. Deliberately duplicates the render
// conditions — the WeekHeatmap jsdom test is what keeps the two honest.
//
// The column header row is not part of the navigation: a weekday label carries
// neither an action nor a tooltip, so it stays plain text instead of becoming a
// focusable element with nothing to offer.
export function buildHeatmapNavRows(
  { skills, staffBySkillId, heatmapCells, staffHeatmapCells }: HeatmapData,
  expandedSkills: Record<string, boolean>
): HeatmapNavRow[] {
  const rows: HeatmapNavRow[] = [];

  for (const skill of skills) {
    const skillCells = heatmapCells.filter((cell) => cell.skillId === skill.id);
    rows.push({ id: skillRowId(skill.id), columns: navigableColumnsOf(skillCells) });

    if (!expandedSkills[skill.id]) continue;

    for (const staff of staffBySkillId[skill.id] ?? []) {
      const staffCells = staffHeatmapCells.filter(
        (cell) => cell.skillId === skill.id && cell.staffId === staff.id
      );
      rows.push({
        id: staffRowId(skill.id, staff.id),
        columns: navigableColumnsOf(staffCells)
      });
    }
  }

  return rows;
}

function locateCell(rows: HeatmapNavRow[], cellId: string): HeatmapCellLocation | null {
  for (const [rowIndex, row] of rows.entries()) {
    const columnIndex = row.columns.findIndex(
      (column) => buildHeatmapCellId(row.id, column) === cellId
    );
    if (columnIndex !== -1) return { rowIndex, columnIndex };
  }
  return null;
}

// The default tab stop: the first reachable cell of the first row that has one.
export function getFirstCellId(rows: HeatmapNavRow[]): string | null {
  const row = rows.find((candidate) => candidate.columns.length > 0);
  return row ? buildHeatmapCellId(row.id, row.columns[0]) : null;
}

export function hasCell(rows: HeatmapNavRow[], cellId: string | null): boolean {
  return cellId !== null && locateCell(rows, cellId) !== null;
}

// Walks away from `rowIndex` in `step` direction for the first row that renders
// the same column, so a vertical move keeps its column and steps over rows
// where that day is closed or that column is absent.
function findCellInColumn(
  rows: HeatmapNavRow[],
  rowIndex: number,
  column: HeatmapNavColumn,
  step: number
): string | null {
  for (let index = rowIndex + step; index >= 0 && index < rows.length; index += step) {
    if (rows[index].columns.includes(column)) return buildHeatmapCellId(rows[index].id, column);
  }
  return null;
}

function cellIdAt(row: HeatmapNavRow, columnIndex: number): string | null {
  const column = row.columns[columnIndex];
  return column ? buildHeatmapCellId(row.id, column) : null;
}

// Resolves the cell a navigation key moves to, or null when there is no move:
// the grid's edge, an already-satisfied Home/End, or an unknown origin. Focus
// never wraps, per the WAI-ARIA grid pattern.
export function getNavTargetCellId(
  rows: HeatmapNavRow[],
  fromCellId: string,
  key: HeatmapNavKey
): string | null {
  const location = locateCell(rows, fromCellId);
  if (!location) return null;

  const row = rows[location.rowIndex];
  switch (key) {
    case 'ArrowRight':
      return cellIdAt(row, location.columnIndex + 1);
    case 'ArrowLeft':
      return cellIdAt(row, location.columnIndex - 1);
    case 'ArrowDown':
      return findCellInColumn(rows, location.rowIndex, row.columns[location.columnIndex], 1);
    case 'ArrowUp':
      return findCellInColumn(rows, location.rowIndex, row.columns[location.columnIndex], -1);
    case 'Home':
      return location.columnIndex === 0 ? null : cellIdAt(row, 0);
    case 'End':
      return location.columnIndex === row.columns.length - 1
        ? null
        : cellIdAt(row, row.columns.length - 1);
  }
}
